'use client';

import { c as _c } from "react/compiler-runtime";
import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

/** Must match `transition: opacity` on `.morfi-podium-halftone::after` in globals.css */
const FADE_MS = 500;
/** Idle time before spotlight fades out */
const IDLE_MS = 300;
const OFFSCREEN_PX = -999;
interface SideTimers {
  idle: ReturnType<typeof setTimeout> | undefined;
  fade: ReturnType<typeof setTimeout> | undefined;
}
function clearTimer(id: ReturnType<typeof setTimeout> | undefined): void {
  if (id !== undefined) {
    clearTimeout(id);
  }
}
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
export interface PodiumHalftoneDecorProps {
  /** Podium `<section>` — spotlight tracks cursor anywhere inside, including over cards. */
  sectionRef: RefObject<HTMLElement | null>;
}

/**
 * Renders both podium halftone wedges and drives spotlight with a single
 * `window` mousemove listener (avoids duplicate layout reads per frame).
 */
export function PodiumHalftoneDecor(t0) {
  const $ = _c(4);
  const {
    sectionRef
  } = t0;
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== sectionRef) {
    t1 = () => {
      const section = sectionRef.current;
      const left = leftRef.current;
      const right = rightRef.current;
      if (section === null || left === null || right === null) {
        return;
      }
      const leftTimers = {
        idle: undefined,
        fade: undefined
      };
      const rightTimers = {
        idle: undefined,
        fade: undefined
      };
      const beginFadeOut = _temp;
      const showSpotlight = (el_0, timers_0, x, y) => {
        clearTimer(timers_0.fade);
        el_0.style.setProperty("--cx", `${x}px`);
        el_0.style.setProperty("--cy", `${y}px`);
        el_0.dataset.hovered = "true";
        clearTimer(timers_0.idle);
        timers_0.idle = setTimeout(() => beginFadeOut(el_0, timers_0), IDLE_MS);
      };
      const updateSideSpotlight = (el_1, timers_1, e) => {
        const rect = el_1.getBoundingClientRect();
        const x_0 = clamp(e.clientX - rect.left, 0, rect.width);
        const y_0 = clamp(e.clientY - rect.top, 0, rect.height);
        showSpotlight(el_1, timers_1, x_0, y_0);
      };
      const onMove = e_0 => {
        const sectionRect = section.getBoundingClientRect();
        const insideSection = e_0.clientX >= sectionRect.left && e_0.clientX <= sectionRect.right && e_0.clientY >= sectionRect.top && e_0.clientY <= sectionRect.bottom;
        if (insideSection) {
          updateSideSpotlight(left, leftTimers, e_0);
          updateSideSpotlight(right, rightTimers, e_0);
        } else {
          clearTimer(leftTimers.idle);
          clearTimer(rightTimers.idle);
          beginFadeOut(left, leftTimers);
          beginFadeOut(right, rightTimers);
        }
      };
      window.addEventListener("mousemove", onMove, {
        passive: true
      });
      return () => {
        window.removeEventListener("mousemove", onMove);
        clearTimer(leftTimers.idle);
        clearTimer(leftTimers.fade);
        clearTimer(rightTimers.idle);
        clearTimer(rightTimers.fade);
      };
    };
    t2 = [sectionRef];
    $[0] = sectionRef;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <><div ref={leftRef} aria-hidden={true} className="morfi-podium-halftone morfi-podium-halftone--left" /><div ref={rightRef} aria-hidden={true} className="morfi-podium-halftone morfi-podium-halftone--right" /></>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function _temp(el, timers) {
  if (el.dataset.hovered !== "true") {
    return;
  }
  el.dataset.hovered = "false";
  clearTimer(timers.fade);
  timers.fade = setTimeout(() => {
    el.style.setProperty("--cx", `${OFFSCREEN_PX}px`);
    el.style.setProperty("--cy", `${OFFSCREEN_PX}px`);
    timers.fade = undefined;
  }, FADE_MS);
}